import { z } from "zod";

import { ADJUST_REASONS } from "@pharmacy/domain";
import { moneyNotNegative, moneyText } from "../money";

/**
 * Products, and the stock recorded against them.
 *
 * ONE place records stock: Add product. You find the medicine, or add it if it
 * is new, then say what arrived — size, how many, cost, price, expiry. Whether
 * it came off a van this morning or has been on the shelf for a year makes no
 * difference; it is recorded the same way, one medicine at a time.
 *
 * Underneath, a product and its stock are still two things. The product is the
 * medicine — its name and the sizes it is sold in, recorded once. Each time
 * stock is recorded it becomes a BATCH with its own expiry date and its own
 * cost, because the 2027 stock and the 2028 stock of the same medicine are not
 * interchangeable. The person using the app never sees that split as two
 * screens; they see a medicine with its batches listed inside it.
 */

/** An ISO calendar date, and nothing else. No times: expiry is a day. */
export const isoDate = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Use a date like 2027-03-31")
  .refine((v) => !Number.isNaN(Date.parse(`${v}T00:00:00Z`)), "That is not a real date");

/**
 * Something picked from a list, or a new name typed in its place.
 *
 * Used for supplier and category, so neither needs a screen of its own before
 * it can be used: tap one you have used before, or type a new name and it is
 * created as part of the same save.
 */
export const pickOrType = z.union([
  z.object({ id: z.string().uuid() }),
  z.object({ name: z.string().trim().min(1, "Enter a name").max(160) }),
]);

/* ------------------------------------------------------------------ sizes */

/**
 * The size stock is being counted in.
 *
 * Either one the medicine already has, or a new one described by what it is
 * called and how many of the smallest unit it holds. Two boxes of the same
 * medicine routinely hold different counts, which is why the count is part of
 * the description rather than assumed from the label.
 *
 * A new size with a count of 1 is the smallest unit itself.
 */
export const sizeInput = z.union([
  z.object({ unitId: z.string().uuid() }),
  z.object({
    label: z.string().trim().min(1, "Name this size").max(30),
    count: z.number().int().positive("How many does it hold?").max(100_000),
    /**
     * Made on the phone, when the stock was recorded with no internet: a sale
     * of this size, made straight after, names it before the server has it.
     * A size already holding that many is used instead, on both sides.
     */
    id: z.string().uuid().optional(),
  }),
]);

/** Adding a size without recording stock — to sell strips out of boxes, say. */
export const addSizeRequest = z.object({
  /** Made on the phone, so a size added offline is sent once and sold by this id. */
  id: z.string().uuid().optional(),
  label: z.string().trim().min(1, "Name this size").max(30),
  count: z.number().int().min(2, "The smallest unit already exists").max(100_000),
  /** Exact decimal text, never a float: 0.1 + 0.2 is not 0.3, and a till cannot afford that. */
  sellPrice: moneyNotNegative("A price cannot be negative"),
});

/**
 * A size's price. Its count is deliberately not editable: every batch counted
 * in that size assumed it, and changing it would rescale history.
 */
export const updateSizeRequest = z.object({
  sellPrice: moneyNotNegative("A price cannot be negative"),
});

/* ------------------------------------------------------------------ stock */

export const stockInput = z.object({
  /**
   * The batch's id, made on the phone: stock recorded with no internet is sent
   * later, perhaps twice on a bad line, and is recorded once.
   */
  batchId: z.string().uuid().optional(),
  /** Which shop it is on the shelf of. */
  branchId: z.string().uuid(),
  size: sizeInput,
  /** How many of that size. */
  qty: z.number().int().positive("How many?").max(1_000_000),
  /**
   * What one of that size cost. Exact, up to ten decimals.
   *
   * Per SIZE rather than per tablet, because that is what the invoice says —
   * nobody knows what one tablet cost; everybody knows what the box cost. Zero
   * is allowed: suppliers give bonus boxes and samples.
   */
  cost: moneyNotNegative("A cost cannot be negative"),
  /**
   * What one of that size sells for.
   *
   * Filled in on the screen from the current price, and changed only when the
   * new stock cost more. Saving it changes that size's price for every sale
   * from now on — one price per size, so two identical boxes on a shelf never
   * cost a customer two different amounts.
   */
  sellPrice: moneyNotNegative("A price cannot be negative"),
  /**
   * Nullable, because a pharmacy also sells soap and razors. Whether it is
   * REQUIRED depends on the product, so the server checks it, not this schema.
   */
  expiryDate: isoDate.nullable().optional(),
  /** The lot number printed on the box. Matters when a medicine is recalled. */
  batchNo: z.string().trim().max(80).nullable().optional(),
  supplier: pickOrType.nullable().optional(),
  /** When it actually arrived, for stock entered the next morning. */
  receivedAt: isoDate.optional(),
  /**
   * Accept stock that is already past its expiry date.
   *
   * Off by default, so the server refuses and the screen can ask. On, because
   * a shop that cannot record expired stock has stock on a shelf the system has
   * never heard of — worse than stock it knows is expired. The batch is
   * quarantined either way, so the till warns rather than hides it.
   */
  acceptExpired: z.boolean().optional(),
});

export const addStockRequest = stockInput;

/* ----------------------------------------------------- changing a quantity */

/**
 * Why a quantity was changed by hand. One tap each on the phone.
 *
 * Expired, damaged, missing and returned only take stock DOWN; counted wrong
 * and other can go either way. Other always needs a message.
 */
export const adjustReason = z.enum(ADJUST_REASONS);

/**
 * "There are 7 now, because 3 expired."
 *
 * The NEW number rather than the difference, because that is what the person
 * holding the box can see and count. The server works out the difference and
 * records that — nothing is overwritten, so "10, then 3 removed, expired, by
 * Ahmed" stays visible for good.
 *
 * In the smallest unit, the same unit the batch shows its quantity in.
 */
/**
 * "That cost was typed wrong": what one of a size really cost.
 *
 * Per SIZE, as when the stock was recorded — the price on the invoice, not a
 * sum worked out per tablet. The batch's cost changes, and so does what every
 * sale from it already cost, so profit comes out right for the past too.
 */
export const correctCostRequest = z.object({
  unitId: z.string().uuid(),
  cost: moneyNotNegative("A cost cannot be negative"),
});

export const adjustBatchRequest = z
  .object({
    /** Made on the phone: a change sent twice on a bad line is made once. */
    id: z.string().uuid().optional(),
    newQtyBase: z.number().int("Enter a whole number").min(0, "It cannot be less than zero").optional(),
    /**
     * Or the difference from what the phone showed — how the phone sends it,
     * as the owner agreed: "saw 10, made it 7" is −3, so sales other phones
     * made in the meantime are never undone. Never below none.
     */
    deltaBase: z.number().int("Enter a whole number").optional(),
    reason: adjustReason,
    note: z.string().trim().max(300).nullable().optional(),
  })
  .refine((v) => (v.newQtyBase === undefined) !== (v.deltaBase === undefined), "Give the new number or the difference");

/**
 * "Count the stock" for a whole product (DukaanBook's Product page, where
 * batches are not shown): the number really on the shelf, in the smallest
 * unit, and why it differs. The server lays the difference on the batches.
 */
export const countStockRequest = z.object({
  /** Made once on the screen: Save pressed twice counts once. */
  id: z.string().uuid().optional(),
  countedBase: z.number().int("Enter a whole number").min(0, "It cannot be less than zero"),
  reason: adjustReason,
  note: z.string().trim().max(300).nullable().optional(),
});

/* ---------------------------------------------------------------- product */

/**
 * A barcode, or nothing.
 *
 * Plenty of stock arrives with no scannable code, so this is never trusted as
 * an identifier. An empty string becomes null: the column is unique per
 * pharmacy where it is not null, and two products saved with "" would collide
 * on a value neither of them has.
 */
export const barcodeField = z
  .string()
  .trim()
  .max(64)
  .transform((v) => (v === "" ? null : v))
  .nullable();

const productFields = {
  /** Brand name as printed on the box. Never translated. */
  name: z.string().trim().min(2, "Enter the product name").max(160),
  /** What a customer asks for — somebody will search for "kaniini". */
  nameSo: z.string().trim().max(160).nullable().optional(),
  /** The molecule, for when the brand is out of stock. */
  genericName: z.string().trim().max(160).nullable().optional(),
  strength: z.string().trim().max(60).nullable().optional(),
  form: z.string().trim().max(60).nullable().optional(),
  manufacturer: z.string().trim().max(120).nullable().optional(),
  originCountry: z.string().trim().max(60).nullable().optional(),
  barcode: barcodeField.optional(),
  category: pickOrType.nullable().optional(),
  /**
   * Medicines must carry an expiry date; soap and razors need not. Defaults
   * true so nothing medicinal is recorded without one by omission.
   */
  requiresExpiry: z.boolean().optional(),
  isRx: z.boolean().optional(),
  /** Below this, in the smallest unit, the product wants restocking. */
  reorderLevelBase: z.number().int().min(0).nullable().optional(),
};

/**
 * A new medicine, together with the first stock of it.
 *
 * One save, not two. The screen goes straight from "what is it" to "what
 * arrived", and saving them separately would leave a medicine with no stock
 * and no price if the second half failed — and a retry would create it twice.
 */
export const createProductRequest = z.object({
  /**
   * The medicine's id and its smallest size's, made on the phone: a medicine
   * added with no internet is sold at once, by these ids, and sent once.
   */
  id: z.string().uuid().optional(),
  baseSizeId: z.string().uuid().optional(),
  ...productFields,
  /**
   * What stock is counted in: tablet, capsule, ml, bottle, piece.
   *
   * Set once. Every stock figure for this medicine is a count of it, so
   * changing it later would silently rescale stock already on a shelf.
   */
  baseUnit: z.string().trim().min(1, "What do you count it in?").max(30),
  stock: stockInput,
});

/** Editing a medicine's details. `baseUnit` is absent on purpose — see above. */
export const updateProductRequest = z
  .object({ ...productFields, isActive: z.boolean().optional() })
  .partial();

export const productSearchQuery = z.object({
  /** Brand, Somali and generic names at once. Trigram, so misspelt is fine. */
  q: z.string().trim().max(120).optional(),
  categoryId: z.string().uuid().optional(),
  /** Archived products stay searchable for history, but not by default. */
  includeInactive: z.coerce.boolean().optional(),
  /** Only the archived ones: Settings → Archived medicines. Send "true" or leave it out. */
  archived: z.coerce.boolean().optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  /**
   * Skip this many: the next page, for a list that shows a page at a time
   * (the web's Products list, "Load more"). The answer's `next` is the offset
   * to ask for after this page, or null at the end.
   */
  offset: z.coerce.number().int().min(0).max(100_000).optional(),
});

/* -------------------------------------------------------------- responses */

export const sizeResponse = z.object({
  id: z.string().uuid(),
  /** Stored with its count: "box (30)". The smallest unit is just its name. */
  name: z.string(),
  /** How many of the smallest unit it holds. */
  multiplier: z.number().int(),
  sellPrice: moneyText,
  /** True for the smallest unit, which every medicine has exactly one of. */
  isBase: z.boolean(),
});

/**
 * What is on the shelf, across the branches this person works in.
 *
 * Cost figures are null for somebody not allowed to see cost prices. The server
 * blanks them; the screen hiding them would be convenience, not a boundary.
 */
export const stockSummaryResponse = z.object({
  /** In the smallest unit. */
  qtyBase: z.number().int(),
  /** Batches with something left in them. */
  batchCount: z.number().int(),
  /**
   * Average cost of one smallest unit, weighted by what is left of each batch.
   *
   * For display only. Each batch keeps its own real cost, and profit is
   * worked out from those — the average never replaces them.
   */
  avgCostPerBase: moneyText.nullable(),
  /** The soonest expiry among batches with stock left. Past means expired stock. */
  nearestExpiry: z.string().nullable(),
  /**
   * Sold before the stock was recorded, in the smallest unit. Zero normally.
   *
   * Already counted in `qtyBase`, which is why that can be below zero. The
   * next stock recorded pays this back first, so it is shown on the medicine
   * as its own line rather than as a batch.
   */
  owedBase: z.number().int(),
});

export const batchResponse = z.object({
  id: z.string().uuid(),
  batchNo: z.string().nullable(),
  expiryDate: z.string().nullable(),
  qtyReceivedBase: z.number().int(),
  qtyLeftBase: z.number().int(),
  /** Null for somebody not allowed to see cost prices. */
  costPerBase: moneyText.nullable(),
  receivedAt: z.string(),
  supplierName: z.string().nullable(),
  status: z.enum(["active", "quarantined", "written_off"]),
  /** The size its cost was typed for when it was recorded, to correct it in the same terms. */
  recordedUnitId: z.string().uuid().nullable(),
});

export const productResponse = z.object({
  id: z.string().uuid(),
  name: z.string(),
  nameSo: z.string().nullable(),
  genericName: z.string().nullable(),
  strength: z.string().nullable(),
  form: z.string().nullable(),
  manufacturer: z.string().nullable(),
  originCountry: z.string().nullable(),
  barcode: z.string().nullable(),
  categoryId: z.string().uuid().nullable(),
  categoryName: z.string().nullable(),
  baseUnit: z.string(),
  requiresExpiry: z.boolean(),
  isRx: z.boolean(),
  reorderLevelBase: z.number().int().nullable(),
  isActive: z.boolean(),
  /** Smallest first. */
  sizes: z.array(sizeResponse),
  stock: stockSummaryResponse,
  /** Batches with stock left, soonest expiry first — the order they sell in. */
  batches: z.array(batchResponse),
  /**
   * The latest quantity changes made by hand, newest first.
   *
   * On the medicine rather than under each batch, because a batch changed to
   * zero drops out of the batch list — and its history would have gone with it,
   * in exactly the case an owner most needs to see: "10 → 0, missing".
   */
  changes: z.array(
    z.object({
      id: z.string().uuid(),
      batchId: z.string().uuid(),
      /** Which batch, told apart the way a person would: by its expiry. */
      batchExpiry: z.string().nullable(),
      batchNo: z.string().nullable(),
      /** Signed: -3 for three removed, +2 for two found. */
      deltaBase: z.number().int(),
      reason: adjustReason,
      note: z.string().nullable(),
      userName: z.string().nullable(),
      at: z.string(),
    }),
  ),
});

/** A row in the products list. Lighter than the full record. */
export const productSummaryResponse = z.object({
  id: z.string().uuid(),
  name: z.string(),
  nameSo: z.string().nullable(),
  genericName: z.string().nullable(),
  strength: z.string().nullable(),
  form: z.string().nullable(),
  baseUnit: z.string(),
  categoryName: z.string().nullable(),
  isRx: z.boolean(),
  isActive: z.boolean(),
  /** Price of the smallest unit, so a row can show what one costs a customer. */
  basePrice: moneyText.nullable(),
  /**
   * Its sizes, by name and count, so a row can say stock in packs: "10 boxes,
   * 5 strips" (2026-10-18). Optional: older servers did not send it.
   */
  packs: z.array(z.object({ name: z.string(), multiplier: z.number().int() })).optional(),
  stock: stockSummaryResponse,
});

export const productSearchResponse = z.object({
  products: z.array(productSummaryResponse),
  /** The offset of the next page, or null when this is the last. */
  next: z.number().int().nullable().optional(),
});

/**
 * GET /products/stock-overview: how many medicines the working branch has,
 * and those out of stock or running low (the shared stockAlerts rule, least
 * left first), worked out across every medicine. The search above returns a
 * page at a time; the phone has its own full copy, the web asks for this.
 */
export const stockOverviewResponse = z.object({
  medicines: z.number().int().nonnegative(),
  out: z.array(productSummaryResponse),
  low: z.array(productSummaryResponse),
});

export type PickOrType = z.infer<typeof pickOrType>;
export type SizeInput = z.infer<typeof sizeInput>;
export type StockInput = z.infer<typeof stockInput>;
export type AddStockRequest = z.infer<typeof addStockRequest>;
export type AddSizeRequest = z.infer<typeof addSizeRequest>;
export type UpdateSizeRequest = z.infer<typeof updateSizeRequest>;
export type CorrectCostRequest = z.infer<typeof correctCostRequest>;
export type AdjustBatchRequest = z.infer<typeof adjustBatchRequest>;
export type CountStockRequest = z.infer<typeof countStockRequest>;
export type AdjustReason = z.infer<typeof adjustReason>;
export type CreateProductRequest = z.infer<typeof createProductRequest>;
export type UpdateProductRequest = z.infer<typeof updateProductRequest>;
export type ProductSearchQuery = z.infer<typeof productSearchQuery>;
export type SizeResponse = z.infer<typeof sizeResponse>;
export type StockSummaryResponse = z.infer<typeof stockSummaryResponse>;
export type BatchResponse = z.infer<typeof batchResponse>;
export type ProductResponse = z.infer<typeof productResponse>;
export type ProductSummaryResponse = z.infer<typeof productSummaryResponse>;
export type StockOverviewResponse = z.infer<typeof stockOverviewResponse>;
